import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, ClipboardList, RotateCcw, Ban, Trash2,
} from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Modal, Input, Select, Badge, Table, EmptyState, Tooltip } from '../ui-v2.5';
import {
  listTareasBaja, crearTareaBajaCatalogo, actualizarTareaBajaCatalogo,
  desactivarTareaBajaCatalogo, eliminarTareaBajaCatalogoPermanente, listAreas, listTiposActivo, listRecursos,
} from './api';
import { parecidos } from './parecidoATarea';
import EliminarModal from './components/EliminarModal';
import IncorporacionesTabs from './IncorporacionesTabs';

/**
 * Tareas fijas de baja — las que se crean en cada baja nueva, con su área.
 *
 * ── EL NUMERO QUE EXPLICA POR QUE EXISTE ESTA PANTALLA ────────────────────────────────────────
 *
 * Medido en producción: de 23 tareas de baja, **20 tienen área y 3 no**. Y las tres sin área son
 * exactamente las tres de finiquito. Las otras veinte las genera el código al revocar accesos, y
 * todas llevan la suya.
 *
 *   > No es que a las tareas les falte área: es que **las únicas que se escriben a mano son las
 *   > que no la llevan**, y justo son las de Administración.
 *
 * El aviso de cierre se reparte POR ÁREA. Una tarea que nace sin ninguna no la ve nadie — por eso
 * aquí el área es obligatoria y el formulario lo dice en esas palabras.
 *
 * ── DESACTIVAR NO ROMPE NADA, Y POR ESO NO HAY RECUENTO DE USO ────────────────────────────────
 *
 * Las tareas se COPIAN a la baja al crearla. Una vez copiada, la de esa baja vive por su cuenta y
 * no apunta aquí. Quitar una del catálogo no deja nada colgando — al revés que un motivo, donde la
 * baja guarda el `motivo_id` y por eso allí sí se cuenta antes de borrar.
 */

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

export default function TareasBaja() {
  const [tareas, setTareas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      const [t, a] = await Promise.all([listTareasBaja(), listAreas()]);
      setTareas((t && t.tareas) || []);
      setAreas((Array.isArray(a) ? a : (a && a.areas) || []).filter(Boolean));
    } catch (e) {
      console.error('[incorp][tareas-baja] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function cambiarActiva(t, activa) {
    setAccionId(t.id);
    try {
      if (activa) await actualizarTareaBajaCatalogo(t.id, { descripcion: t.descripcion, area_id: t.area_id, orden: t.orden, activa: true });
      else await desactivarTareaBajaCatalogo(t.id);
      toast.success(`Tarea «${t.descripcion}» ${activa ? 'reactivada' : 'desactivada'}.`);
      await cargar();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo cambiar el estado de la tarea.');
    } finally {
      setAccionId(null);
    }
  }

  const columns = [
    {
      key: 'descripcion',
      header: 'Tarea',
      render: (t) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${t.activa ? 'text-v25-ink-900' : 'text-v25-ink-400'}`}>{t.descripcion}</span>
          {!t.activa && <Badge tone="expired">Desactivada</Badge>}
        </div>
      ),
    },
    {
      key: 'area',
      header: 'Área responsable',
      width: 200,
      /* EL AREA ES LA COLUMNA QUE IMPORTA: es quien se entera del aviso al cerrar la baja. */
      render: (t) => (t.area
        ? <span className="text-v25-ink-700">{t.area.nombre}</span>
        : <span className="text-amber-700">⚠ sin área</span>),
    },
    {
      key: 'acciones',
      header: '',
      width: 190,
      render: (t) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditor({ tarea: t })}><Pencil />Editar</Button>
          {t.activa ? (
            <Tooltip content="Desactivar: deja de crearse en las bajas nuevas; las ya creadas no se tocan">
              <Button variant="ghost" size="icon" disabled={accionId === t.id} onClick={() => cambiarActiva(t, false)}>
                {accionId === t.id ? <Loader2 className="animate-spin" /> : <Ban />}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip content="Reactivar">
              <Button variant="ghost" size="icon" disabled={accionId === t.id} onClick={() => cambiarActiva(t, true)}>
                {accionId === t.id ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              </Button>
            </Tooltip>
          )}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
          <Tooltip content="Eliminar permanentemente">
            <Button variant="ghost" size="icon" onClick={() => setAEliminar(t)} className="text-red-600 hover:bg-red-50 hover:text-red-700">
              <Trash2 />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Tareas de baja"
        subtitle="Las que se crean solas en cada baja nueva, con el área que las recibe."
        action={<Button onClick={() => setEditor({ tarea: null })}><Plus />Nueva tarea</Button>}
      />

      <IncorporacionesTabs active="tareas" />

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando tareas…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar las tareas.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : tareas.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={ClipboardList}
          title="Aún no hay tareas fijas"
          description="Crea las que se hacen en todas las bajas —por ejemplo el pago del finiquito— y elige el área que las recibe."
          action={<Button onClick={() => setEditor({ tarea: null })}><Plus />Nueva tarea</Button>}
        />
      ) : (
        <Table columns={columns} rows={tareas} />
      )}

      {editor && (
        <TareaModal
          tarea={editor.tarea}
          areas={areas}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar tarea"
          subtitulo={aEliminar.descripcion}
          exitoMsg={`Tarea «${aEliminar.descripcion}» eliminada permanentemente.`}
          eliminar={() => eliminarTareaBajaCatalogoPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará <strong>«{aEliminar.descripcion}»</strong> del catálogo. Las bajas que ya la
          tengan creada <strong>no se tocan</strong>: la tarea se copia al crear la baja y desde
          entonces vive por su cuenta.
        </EliminarModal>
      )}
    </div>
  );
}

function TareaModal({ tarea, areas, onClose, onGuardado }) {
  const editando = !!tarea;
  const [descripcion, setDescripcion] = useState(tarea?.descripcion || '');
  const [areaId, setAreaId] = useState(tarea?.area_id ? String(tarea.area_id) : '');
  const [orden, setOrden] = useState(tarea?.orden != null ? String(tarea.orden) : '');
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [reactivable, setReactivable] = useState(null);

  /* LO QUE CADA BAJA GENERA SOLA (18/10). Una tarea de este catálogo se copia en CADA baja nueva: si
   * repite la de una pieza o un acceso, se suma a la suya en todas. Pasó en TEST con «Devolución
   * movil». Quien está aquí no tiene por qué saber que el material y los accesos se generan solos, y
   * por eso lo añade: el aviso dice QUÉ se genera, no sólo que no se escriba. Avisa; no impide. */
  const [tipos, setTipos] = useState([]);
  const [accesos, setAccesos] = useState([]);
  useEffect(() => {
    let vivo = true;
    listTiposActivo().then((r) => { if (vivo) setTipos(((r && r.tipos) || []).map((x) => x.nombre)); }).catch(() => {});
    listRecursos().then((r) => { if (vivo) setAccesos((Array.isArray(r) ? r : []).filter((x) => x.activo && x.tipo === 'credencial').map((x) => x.nombre)); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const unicos = (xs) => [...new Set(xs)];
  const generadas = [
    ...unicos(tipos).map((n) => ({ etiqueta: n, textos: [n], donde: 'material' })),
    ...unicos(accesos).map((n) => ({ etiqueta: n, textos: [n], donde: 'acceso' })),
  ];
  const repiteA = parecidos(descripcion, generadas);
  const muestra = (xs) => { const u = unicos(xs); return u.length > 4 ? `${u.slice(0, 4).join(', ')}…` : u.join(', '); };

  /* Las áreas DESACTIVADAS no se ofrecen —el servidor las rechaza igual—, pero si la tarea que se
   * edita apunta a una, se deja en la lista para no cambiársela sin querer al guardar otra cosa. */
  const opciones = (() => {
    const activas = areas.filter((a) => a.activa);
    const actual = areas.find((a) => String(a.id) === String(tarea?.area_id));
    return actual && !actual.activa ? [actual, ...activas] : activas;
  })();

  function validar() {
    const e = {};
    if (!descripcion.trim()) e.descripcion = 'La descripción es obligatoria.';
    else if (descripcion.trim().length > 255) e.descripcion = 'Máximo 255 caracteres.';
    if (!areaId) e.area = 'Elige el área: el aviso de cierre se reparte por área, y una tarea sin área no la ve nadie.';
    if (orden !== '' && !/^\d+$/.test(orden)) e.orden = 'El orden debe ser un entero ≥ 0.';
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  async function guardar() {
    if (!validar()) return;
    setGuardando(true);
    setReactivable(null);
    try {
      const payload = { descripcion: descripcion.trim(), area_id: Number(areaId), orden: orden === '' ? 0 : Number(orden) };
      if (editando) await actualizarTareaBajaCatalogo(tarea.id, payload);
      else await crearTareaBajaCatalogo(payload);
      toast.success(`Tarea «${payload.descripcion}» ${editando ? 'actualizada' : 'creada'}.`);
      onGuardado();
    } catch (e) {
      const d = cuerpoError(e);
      if (d.error === 'descripcion_duplicada_inactiva') setReactivable({ id: d.id, mensaje: d.message });
      else if (d.error === 'descripcion_duplicada') setErrores({ descripcion: d.message });
      else if (d.error === 'area_desactivada' || d.error === 'area_no_encontrada') setErrores({ area: d.message });
      else toast.error(d.message || 'No se pudo guardar la tarea.');
    } finally {
      setGuardando(false);
    }
  }

  async function reactivarDuplicada() {
    setGuardando(true);
    try {
      await actualizarTareaBajaCatalogo(reactivable.id, {
        descripcion: descripcion.trim(), area_id: Number(areaId), orden: orden === '' ? 0 : Number(orden), activa: true,
      });
      toast.success('Tarea reactivada.');
      onGuardado();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo reactivar la tarea.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editando ? 'Editar tarea' : 'Nueva tarea'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
          {reactivable ? (
            <Button onClick={reactivarDuplicada} disabled={guardando}>
              {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar la existente
            </Button>
          ) : (
            <Button onClick={guardar} disabled={guardando}>
              {guardando ? <Loader2 className="animate-spin" /> : null}{editando ? 'Guardar' : 'Crear'}
            </Button>
          )}
        </>
      )}
    >
      <div className="space-y-3">
        <Input
          label="Descripción"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          error={errores.descripcion}
          placeholder="Pago finiquito"
        />
        {repiteA.length > 0 && (
          <div className="rounded-v25-md border border-v25-waiting-bd bg-v25-waiting-bg px-3 py-2 text-xs text-v25-waiting-fg" role="status">
            <p>
              Se parece a {repiteA.map((x, i) => <React.Fragment key={x.etiqueta}>{i ? ', ' : ''}<strong>{x.etiqueta}</strong> ({x.donde})</React.Fragment>)}, y eso
              <strong> cada baja ya lo genera sola</strong>:
            </p>
            <ul className="mt-1 list-disc pl-4">
              <li>una tarea para recuperar <strong>cada pieza que la persona tenga en Inventario</strong>{tipos.length ? <> ({muestra(tipos)})</> : null};</li>
              <li>y una para revocar <strong>cada acceso</strong>{accesos.length ? <> ({muestra(accesos)})</> : null}.</li>
            </ul>
            <p className="mt-1">Una tarea de este catálogo se copia en <strong>todas</strong> las bajas: si es la misma, se sumaría a la suya en cada una. Si es otra cosa —«el portátil del almacén, no el suyo»—, guárdala igual.</p>
          </div>
        )}
        <Select
          label="Área responsable"
          value={areaId}
          onChange={(e) => setAreaId(e.target.value)}
          error={errores.area}
          hint="Quien recibe el aviso al cerrar la baja. Sin área, la tarea no la ve nadie."
        >
          <option value="">Seleccionar…</option>
          {opciones.map((a) => (
            <option key={a.id} value={a.id}>{a.nombre}{a.activa ? '' : ' (desactivada)'}</option>
          ))}
        </Select>
        <Input
          label="Orden"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          error={errores.orden}
          hint="Posición en la lista de tareas de la baja. Menor, antes."
          placeholder="10"
        />
        {reactivable && <p className="text-sm text-amber-700">{reactivable.mensaje}</p>}
      </div>
    </Modal>
  );
}
